/**
 * The four vitals: which way each one points, and where its live value comes from.
 *
 * **Polarity is the whole file.** Drawing all four with one bar that fills as the number
 * rises is not a styling complaint — it is wrong. `health: 58` and
 * `hunger: 58` sit next to each other in the seeded character (`mocks/seed/world.ts`), the
 * same number and opposite news, and a bar that fills as hunger rises tells the player they
 * are 58% of the way to *something good* while the engine walks them toward a death at 100.
 * So `polarityOf` is consulted by every reading of a vital, and the number itself is left
 * exactly as the engine reports it: the player and the log say `hunger 58`, and only the
 * picture changes.
 *
 * **What this module refuses to do.** It does not know where a band begins. `Vital.band`,
 * `band_label` and `note` arrive display-joined from the API (U1) and are rendered, never
 * recomputed — the character panel says so in as many words, and the reason is that a threshold copied into
 * the client is a threshold that silently disagrees with `rules.py` the first time the engine
 * is tuned. The one consequence worth stating out loud is in `readVitals` below.
 */
import type { Change, Vital, Vitals } from '@/api/generated';
import type { UiKey } from '@/i18n/en.generated';

export const VITAL_NAMES = ['health', 'stamina', 'hunger', 'thirst'] as const;
export type VitalName = (typeof VITAL_NAMES)[number];

/**
 * `up` — more is better, and the bar is a *quantity* that drains from full.
 * `down` — more is worse, and the bar is a *warning* that grows toward a death at 100.
 */
export type VitalPolarity = 'up' | 'down';

const POLARITY: Record<VitalName, VitalPolarity> = {
  health: 'up',
  stamina: 'up',
  hunger: 'down',
  thirst: 'down',
};

export function polarityOf(name: VitalName): VitalPolarity {
  return POLARITY[name];
}

/** Sentence-case, for the bar's own label. The API does not send one for the vital itself. */
const VITAL_LABEL_KEYS: Record<VitalName, UiKey> = {
  health: 'ui.vital.health',
  stamina: 'ui.vital.stamina',
  hunger: 'ui.vital.hunger',
  thirst: 'ui.vital.thirst',
};

/**
 * The key, not the word — the bar's own name is chrome. The *band* beside it (`Badly wounded`)
 * is `vital.band_label`, which the API joined from `vitals.bands.*` on its way out (U1), so the
 * two halves of that row come from different sides of the wire on purpose.
 */
export function vitalLabelKey(name: VitalName): UiKey {
  return VITAL_LABEL_KEYS[name];
}

/**
 * A vital as the panel draws it: the server's `Vital` plus whatever a running turn has since
 * done to the number.
 */
export interface VitalReading {
  name: VitalName;
  /** Display truth from the API: `band`, `band_label`, `note` (U1). Never recomputed. */
  vital: Vital;
  /** What the bar draws. `vital.value` at rest; the live value while a turn streams. */
  value: number;
  /** Signed, `after − before`. Non-null only when a live change moved it this turn. */
  delta: number | null;
  /**
   * The live value has outrun the band the server sent, so `band_label` and `note` describe
   * where the character *was* at the top of the turn. See `readVitals`.
   */
  pending: boolean;
  /**
   * Identity of the change that last moved this vital, so the flyout can replay per change
   * rather than per render. Null when nothing moved it.
   */
  movedBy: number | null;
}

/**
 * How the engine writes a vital `change`, read defensively.
 *
 * Every one of the eight sites in `quest/engine/engine.py` that touches a vital records
 * **`after`** — the post-change value — and then either `before` (damage, heal, stamina, eat,
 * drink) or `delta` (the passive hunger/thirst rise in `settle`). So `after` is the field to
 * trust and the other two are how the delta gets named.
 *
 * `after` being *absolute* rather than a delta is what makes the live overlay safe. Applying
 * the same change twice is a no-op, which matters at exactly one moment: `commitTurn` patches
 * the character snapshot while the store still holds the turn's changes, and a delta-based
 * overlay would double every bar for that frame.
 */
export function readVitalChange(
  change: Change,
): { name: VitalName; value: number; delta: number | null } | null {
  if (!isVitalName(change.kind)) return null;

  const after = numberField(change.data, 'after');
  if (after === null) return null;

  const before = numberField(change.data, 'before');
  const delta = before !== null ? after - before : numberField(change.data, 'delta');

  return { name: change.kind, value: after, delta };
}

/**
 * The four bars, with a running turn's changes laid over the snapshot (the character panel's last checkbox).
 *
 * The live turn collects `change` events into `turn/store.ts` as they arrive, **during Phase A** —
 * thirty seconds before the first narration token. Passing them here is what makes the bar
 * drop *then* instead of after the turn, and it is the difference between a stat block beside
 * the story and a character in it. Nothing here refetches; the snapshot moves on commit
 * because `commitTurn` patches the state cache from the `chapter` event's own snapshot (P1).
 *
 * **The band lags the value, deliberately.** A live change carries a number, not a band, and
 * this module may not turn one into the other. So during a turn the bar's *geometry* is live
 * and its *label* is the last thing the server said — hunger can be drawn at 74 while it
 * still reads "Very hungry", for the fifty seconds until the `chapter` event settles it. The
 * alternative is a copy of `rules.py`'s thresholds in TypeScript, which would be right until
 * the day somebody tunes the engine and nobody thinks to grep the frontend. `pending` marks
 * the rows where that gap is open so a caller can say so rather than pretend.
 */
export function readVitals(vitals: Vitals, live: readonly Change[] = []): VitalReading[] {
  const moves = new Map<VitalName, { value: number; delta: number | null; at: number }>();

  live.forEach((change, index) => {
    const move = readVitalChange(change);
    if (move === null) return;
    // Last write wins: two `thirst` changes in one turn (a drink, then the passive rise) are
    // both true, and the second one already accounts for the first.
    moves.set(move.name, { value: move.value, delta: move.delta, at: index });
  });

  return VITAL_NAMES.map((name) => {
    const vital = vitals[name];
    const move = moves.get(name);
    return {
      name,
      vital,
      value: move?.value ?? vital.value,
      delta: move?.delta ?? null,
      pending: move !== undefined && move.value !== vital.value,
      movedBy: move?.at ?? null,
    };
  });
}

function isVitalName(kind: string): kind is VitalName {
  return (VITAL_NAMES as readonly string[]).includes(kind);
}

/** `Change.data` is `dict[str, Any]` on the wire; nothing in it is guaranteed to be a number. */
function numberField(data: Record<string, unknown>, key: string): number | null {
  const value = data[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
